import { create } from 'zustand';
import { authAPI } from '../services/api';

interface User {
  id: string;
  email: string;
  username?: string;
  created_at: string;
  last_login?: string;
  is_active: boolean;
  metadata?: any;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.login(email, password);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Get user info
      const userResponse = await authAPI.getCurrentUser();
      set({
        user: userResponse,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },
  
  register: async (email: string, password: string, username?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.register(email, password, username);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Registration failed',
        isLoading: false,
      });
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    set({
      user: null,
      isAuthenticated: false,
      error: null,
    });
  },
  
  checkAuth: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      set({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }
    
    set({ isLoading: true });
    try {
      const userResponse = await authAPI.getCurrentUser();
      set({
        user: userResponse,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: any) {
      // Only clear auth on actual authentication errors, not network errors
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR';
      
      if (isAuthError) {
        // Token is actually invalid - clear it
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } else if (isNetworkError) {
        // Network error - keep existing auth state but stop loading
        set({ isLoading: false });
        console.warn('Network error during auth check - keeping existing auth state');
      } else {
        // Other errors - log but don't clear auth immediately
        console.error('Error during auth check:', error);
        set({ isLoading: false });
      }
    }
  },
  
  clearError: () => set({ error: null }),
}));
